import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { downloadInvoicePdf } from "@/lib/pdf";
import AccountantStatement from "@/components/AccountantStatement";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Landmark, Building2, FileSpreadsheet, Download, Tag, Users, Plus, Trash2, Globe } from "lucide-react";
import { toast } from "sonner";

const emptyBank = {
  account_name: "",
  bank_name: "",
  account_number: "",
  sort_code: "",
  iban: "",
  payment_terms: "",
};

const emptyCompany = {
  name: "J&L Industrial Supplies",
  address: "",
  email: "",
  phone: "",
  vat_number: "",
  company_number: "",
  website: "",
};

export default function Settings() {
  const [bank, setBank] = useState(emptyBank);
  const [company, setCompany] = useState(emptyCompany);
  const [appUrl, setAppUrl] = useState("");
  const [saving, setSaving] = useState(false);

  const thisMonth = () => new Date().toISOString().slice(0, 7);
  const [month, setMonth] = useState(thisMonth());
  const [report, setReport] = useState(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const reportRef = useRef(null);

  const [categories, setCategories] = useState([]);
  const [newCategory, setNewCategory] = useState("");
  const [staff, setStaff] = useState([]);
  const [newStaff, setNewStaff] = useState("");

  const loadSettings = () =>
    api.get("/settings").then(({ data }) => {
      setBank({ ...emptyBank, ...data.bank_details });
      setCompany({ ...emptyCompany, ...data.company_details });
      setCategories(data.expense_categories || []);
      setStaff(data.staff || []);
      setAppUrl(data.app_url || "");
    });

  const saveCategories = async (list) => {
    try {
      await api.put("/settings/categories", { categories: list });
      setCategories(list);
      toast.success("Categories updated");
    } catch {
      toast.error("Failed to update categories");
    }
  };
  const addCategory = () => {
    const c = newCategory.trim();
    if (!c) return;
    if (categories.includes(c)) return toast.error("Category already exists");
    saveCategories([...categories, c]);
    setNewCategory("");
  };
  const removeCategory = (c) => saveCategories(categories.filter((x) => x !== c));
  const renameCategory = (idx, value) =>
    setCategories((prev) => prev.map((c, i) => (i === idx ? value : c)));

  const addStaff = async () => {
    const n = newStaff.trim();
    if (!n) return;
    try {
      const { data } = await api.post("/settings/staff", { name: n });
      setStaff(data.staff || []);
      setNewStaff("");
      toast.success("Staff added");
    } catch {
      toast.error("Failed to add staff");
    }
  };
  const editStaff = async (id, name) => {
    try {
      const { data } = await api.put(`/settings/staff/${id}`, { name });
      setStaff(data.staff || []);
    } catch {
      toast.error("Failed to update staff");
    }
  };
  const removeStaff = async (id) => {
    try {
      const { data } = await api.delete(`/settings/staff/${id}`);
      setStaff(data.staff || []);
      toast.success("Staff removed");
    } catch {
      toast.error("Failed to remove staff");
    }
  };

  const exportAccountant = async () => {
    setExporting(true);
    try {
      const { data } = await api.get("/reports/accountant", { params: { month } });
      setReport(data);
      setReportOpen(true);
    } catch {
      toast.error("Failed to build statement");
    } finally {
      setExporting(false);
    }
  };

  const downloadReport = async () => {
    setDownloading(true);
    try {
      await downloadInvoicePdf(reportRef.current, `Accountant-Statement-${month}.pdf`);
      toast.success("Statement downloaded");
    } catch {
      toast.error("Could not generate PDF");
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { company_details: company, bank_details: bank, app_url: appUrl });
      toast.success("Settings saved — details will show on invoices");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const companyField = (key, label, placeholder) => (
    <div>
      <Label>{label}</Label>
      <Input
        data-testid={`company-${key.replace("_", "-")}-input`}
        value={company[key]}
        onChange={(e) => setCompany({ ...company, [key]: e.target.value })}
        placeholder={placeholder}
      />
    </div>
  );

  const bankField = (key, label, placeholder) => (
    <div>
      <Label>{label}</Label>
      <Input
        data-testid={`bank-${key.replace("_", "-")}-input`}
        value={bank[key]}
        onChange={(e) => setBank({ ...bank, [key]: e.target.value })}
        placeholder={placeholder}
      />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto" data-testid="settings-page">
      <h1 className="font-heading text-3xl font-bold text-[#032352]">Admin Settings</h1>
      <p className="text-[#5B5C5F] mt-1 mb-8">
        Your company details and bank account appear on every invoice.
      </p>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-2 mb-6">
          <Building2 className="w-5 h-5 text-[#032352]" />
          <h3 className="font-heading font-semibold text-[#032352]">Company Details</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">{companyField("name", "Company Name", "J&L Industrial Supplies")}</div>
          <div className="sm:col-span-2">
            <Label>Company Address</Label>
            <Textarea
              data-testid="company-address-input"
              value={company.address}
              onChange={(e) => setCompany({ ...company, address: e.target.value })}
              placeholder="Unit 1, Example Industrial Estate, Town, Postcode"
            />
          </div>
          {companyField("email", "Email", "sales@jlindustrial.co.uk")}
          {companyField("phone", "Phone", "01234 567890")}
          {companyField("vat_number", "VAT Number", "GB 123 4567 89")}
          {companyField("company_number", "Company Reg. No.", "01234567")}
          <div className="sm:col-span-2">{companyField("website", "Website (optional)", "www.jlindustrial.co.uk")}</div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6">
        <div className="flex items-center gap-2 mb-6">
          <Landmark className="w-5 h-5 text-[#032352]" />
          <h3 className="font-heading font-semibold text-[#032352]">Bank Account Details</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="sm:col-span-2">{bankField("account_name", "Account Name", "J&L Industrial Supplies Ltd")}</div>
          {bankField("bank_name", "Bank Name", "Barclays")}
          {bankField("sort_code", "Sort Code", "20-00-00")}
          {bankField("account_number", "Account Number", "12345678")}
          {bankField("iban", "IBAN (optional)", "GB00 BARC 0000 0000 0000 00")}
          <div className="sm:col-span-2">
            <Label>Payment Terms / Notes (optional)</Label>
            <Textarea
              data-testid="bank-payment-terms-input"
              value={bank.payment_terms}
              onChange={(e) => setBank({ ...bank, payment_terms: e.target.value })}
              placeholder="Payment due within 30 days. Please quote the invoice number as reference."
            />
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6">
        <div className="flex items-center gap-2 mb-4">
          <Globe className="w-5 h-5 text-[#032352]" />
          <h3 className="font-heading font-semibold text-[#032352]">App URL</h3>
        </div>
        <p className="text-sm text-[#5B5C5F] mb-4">
          Your deployed domain — used when generating "Sign Delivery Note" links for clients. 
          Set this to your live domain (e.g. <span className="font-mono text-xs">https://accounts.jandlindustrialsupplies.com</span>), not the preview URL.
        </p>
        <div>
          <Label>Base URL</Label>
          <Input
            data-testid="app-url-input"
            value={appUrl}
            onChange={(e) => setAppUrl(e.target.value)}
            placeholder="https://accounts.jandlindustrialsupplies.com"
            className="font-mono text-sm"
          />
        </div>
      </div>

      <div className="flex justify-end mt-6">
        <Button
          data-testid="save-settings-button"
          onClick={save}
          disabled={saving}
          className="bg-[#032352] hover:bg-[#04347A]"
        >
          Save Settings
        </Button>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="categories-card">
        <div className="flex items-center gap-2 mb-4">
          <Tag className="w-5 h-5 text-[#032352]" />
          <h3 className="font-heading font-semibold text-[#032352]">Expense Categories</h3>
        </div>
        <div className="space-y-2 mb-4">
          {categories.map((c, idx) => (
            <div key={idx} className="flex items-center gap-2" data-testid={`category-row-${idx}`}>
              <Input
                data-testid={`category-input-${idx}`}
                value={c}
                onChange={(e) => renameCategory(idx, e.target.value)}
                onBlur={() => saveCategories(categories)}
              />
              <button
                data-testid={`remove-category-${idx}`}
                onClick={() => removeCategory(c)}
                className="text-slate-400 hover:text-red-500 transition-colors shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="new-category-input"
            value={newCategory}
            onChange={(e) => setNewCategory(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addCategory()}
            placeholder="Add a category..."
          />
          <Button data-testid="add-category-button" onClick={addCategory} className="bg-[#032352] hover:bg-[#04347A]">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mt-6" data-testid="staff-card">
        <div className="flex items-center gap-2 mb-4">
          <Users className="w-5 h-5 text-[#032352]" />
          <h3 className="font-heading font-semibold text-[#032352]">Staff</h3>
        </div>
        <p className="text-sm text-[#5B5C5F] mb-4">
          Add staff so you can assign expenses to a person (or mark as a Joint Expense).
        </p>
        <div className="space-y-2 mb-4">
          {staff.length === 0 && <p className="text-sm text-[#5B5C5F]">No staff added yet.</p>}
          {staff.map((m) => (
            <div key={m.id} className="flex items-center gap-2" data-testid={`staff-row-${m.id}`}>
              <Input
                data-testid={`staff-input-${m.id}`}
                defaultValue={m.name}
                onBlur={(e) => e.target.value.trim() && e.target.value !== m.name && editStaff(m.id, e.target.value.trim())}
              />
              <button
                data-testid={`remove-staff-${m.id}`}
                onClick={() => removeStaff(m.id)}
                className="text-slate-400 hover:text-red-500 transition-colors shrink-0"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="flex gap-2">
          <Input
            data-testid="new-staff-input"
            value={newStaff}
            onChange={(e) => setNewStaff(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addStaff()}
            placeholder="Add staff member, e.g. John Smith"
          />
          <Button data-testid="add-staff-button" onClick={addStaff} className="bg-[#032352] hover:bg-[#04347A]">
            <Plus className="w-4 h-4" />
          </Button>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mt-8">
        <div className="flex items-center gap-2 mb-4">
          <FileSpreadsheet className="w-5 h-5 text-[#032352]" />
          <h3 className="font-heading font-semibold text-[#032352]">Export for the Accountant</h3>
        </div>
        <p className="text-sm text-[#5B5C5F] mb-4">
          Produce a monthly statement of all incomings (green) and outgoings (red).
        </p>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label>Month</Label>
            <Input
              type="month"
              data-testid="accountant-month-input"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="w-48"
            />
          </div>
          <Button
            data-testid="export-accountant-button"
            onClick={exportAccountant}
            disabled={exporting}
            className="bg-[#032352] hover:bg-[#04347A]"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" /> {exporting ? "Building…" : "Export for Accountant"}
          </Button>
        </div>
      </div>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto p-0" data-testid="accountant-dialog">
          <DialogTitle className="sr-only">Accountant Statement</DialogTitle>
          <DialogDescription className="sr-only">Monthly incomings and outgoings</DialogDescription>
          <div className="flex justify-end p-4 pb-0">
            <Button
              variant="outline"
              size="sm"
              onClick={downloadReport}
              disabled={downloading}
              data-testid="download-accountant-button"
              className="border-[#032352] text-[#032352]"
            >
              <Download className="w-4 h-4 mr-2" /> {downloading ? "Generating…" : "Download PDF"}
            </Button>
          </div>
          <div ref={reportRef}>
            <AccountantStatement report={report} />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
